import { useState } from "react";
import { ChevronRight, CalendarCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BodyHeatmap } from "@/components/BodyHeatmap";
import { groupIntensity } from "@/storage/weeklyGoal";

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

function WeekDetail({ week, onOpenChange }) {
  const counts = week?.volumeByGroup || {};
  const intensity = groupIntensity(counts);
  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <Dialog open={!!week} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
            Semana {week?.week}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Resumen de la semana {week?.week}
          </DialogDescription>
        </DialogHeader>

        {/* Cifras en columnas en vez de una frase: a 360 px la frase se partía
            en dos renglones y costaba leer los números. */}
        <div className="grid grid-cols-3 gap-2 -mt-1">
          {[
            // El "kg" va en un tamaño menor pegado a la cifra: a 320 px, con la
            // unidad al mismo cuerpo se truncaba el número, y metida en la
            // etiqueta se truncaba la etiqueta.
            ["Series", week?.totalSets ?? 0],
            [
              "Volumen",
              <>
                {week?.totalVolume?.toLocaleString("es-ES") ?? 0}
                <span className="text-[9px] font-semibold text-muted-foreground ml-0.5">kg</span>
              </>,
            ],
            ["Terminada", formatDate(week?.completedAt) || "—"],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl bg-secondary/25 px-1.5 py-2 text-center min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                {label}
              </p>
              <p className="text-[13px] sm:text-sm font-bold text-foreground tabular-nums mt-0.5 truncate">
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* Mismo resumen que el de la semana en curso, pero congelado: el
            volumen por grupo se archivó al cerrar el ciclo, así que el mapa
            refleja aquella semana y no la actual. */}
        {ranked.length > 0 && (
          <div className="space-y-3 pb-4 border-b border-border/30">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Volumen por músculo
            </p>
            <BodyHeatmap intensity={intensity} counts={counts} compact />
            <div className="flex flex-wrap gap-1.5">
              {ranked.map(([group, count]) => (
                <Badge
                  key={group}
                  className="bg-secondary/50 text-foreground/80 border-0 rounded-full text-[10px] font-semibold"
                >
                  {group} · {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {week?.days.map((day, i) => (
            <div key={i} className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                {day.name}
              </p>

              {day.exercises.map((ex, j) => (
                <div key={j} className="flex gap-2.5 p-2.5 rounded-2xl bg-secondary/25">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary/50 flex-shrink-0">
                    {ex.img && (
                      <img src={ex.img} alt="" loading="lazy" className="w-full h-full object-contain" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{ex.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {ex.sets.map((s, k) => (
                        <span
                          key={k}
                          className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md bg-secondary/60 text-foreground/80"
                        >
                          {s.weight || "—"}
                          {s.weight ? "kg" : ""}
                          {s.reps ? ` × ${s.reps}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WeekHistory({ weeks }) {
  const [openWeek, setOpenWeek] = useState(null);

  if (!weeks || weeks.length === 0) {
    return (
      <Card className="glass-surface rounded-3xl border-0 p-6 flex flex-col items-center text-center">
        <CalendarCheck className="h-7 w-7 text-muted-foreground/40 mb-3" />
        <p className="text-foreground/80 font-semibold text-sm">Aún no has cerrado ninguna semana</p>
        <p className="text-muted-foreground text-xs mt-1.5 leading-relaxed max-w-[240px]">
          Cuando marques todas las series de todos los días podrás cerrar el ciclo y empezar
          el siguiente.
        </p>
      </Card>
    );
  }

  return (
    <>
      {/* Más recientes arriba: es lo que se quiere comparar con la semana en curso. */}
      <div className="space-y-2">
        {[...weeks].reverse().map((w) => (
          <button
            key={w.week}
            onClick={() => setOpenWeek(w)}
            className="w-full flex items-center gap-3 p-4 rounded-2xl glass-surface hover:bg-secondary/40 transition-colors text-left"
          >
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground">Semana {w.week}</p>
              <p className="text-[11px] text-muted-foreground">
                {formatDate(w.completedAt)} · {w.days.length}{" "}
                {w.days.length === 1 ? "día" : "días"}
              </p>
            </div>

            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <Badge className="bg-primary/15 text-primary border-0 rounded-full text-[10px] font-semibold">
                {w.totalSets} series
              </Badge>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {w.totalVolume?.toLocaleString("es-ES")} kg
              </span>
            </div>

            <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
          </button>
        ))}
      </div>

      <WeekDetail week={openWeek} onOpenChange={(v) => !v && setOpenWeek(null)} />
    </>
  );
}
